import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import moment from "moment/moment";
import { Select } from "antd";
import MySegmented from "../../components/segmented/Segmented.jsx";
import columns from "../../columns/reports/paymentsReport.js";
import SkeletonTable from "../../components/table/SkeletonTable.jsx";
import {
  GetPaymentReportApi,
  ApprovePaymentApi,
} from "../../api/request/report.js";
import { getAllServiceApi } from "../../api/request/service";
import RejectPaymentModal from "./components/RejectPaymentModal.jsx";
import ConfirmationModal from "../../components/modals/ConfirmationModal.jsx";
import UserAvatar from "../../components/common/UserAvatar.jsx";
import Search from "../../components/common/Search.jsx";
import View from "../../assets/View.jsx";
import GlobalTooltip from "../../components/common/GlobalTooltip.jsx";
import YearPicker from "../../components/common/YearPicker.jsx";
import { useSelector } from "react-redux";

const PaymentsReport = () => {
  const { state } = useLocation();
  const [segmentedValue, setSegmentedValue] = useState(
    state?.type || "interac"
  );
  const [paymentList, setPaymentList] = useState([]);
  const [total, setTotal] = useState(0);
  const [count, setCount] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [serviceList, setServiceList] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [isApproveLoading, setIsApproveLoading] = useState(false);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState(moment().year());
  const [filter, setFilter] = useState(null);
  const userDetails = useSelector((state) => state?.user?.userDetails);
  const paymentType = [
    // { label: `Card (${count?.card_count || 0})`, value: "card" },
    {
      label: `Interac e-Transfers (${count?.interac_count || 0})`,
      value: "interac",
    },
  ];

  const getServiceList = async () => {
    try {
      setIsLoading(true);
      const { data } = await getAllServiceApi();
      if (data?.status && data?.data?.length) {
        const services = data?.data?.map((item) => ({
          value: item?.id,
          label: item?.name,
        }));
        setServiceList([{ value: "all", label: "All Services" }, ...services]);
        setSelectedService("all");
      }
    } catch (error) {
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  const getPaymentList = async (searchText) => {
    try {
      setIsLoading(true);
      const params = {
        ...(selectedService !== "all" && { service_type_id: selectedService }),
        type: segmentedValue,
        page: currentPage,
        status: filter?.statusText || [],
        search: searchText,
        year: selectedYear,
      };
      const { data } = await GetPaymentReportApi(params);
      if (data?.status) {
        setPaymentList(
          data?.data?.map((item) => {
            return {
              ...item,
              userDetails: <UserAvatar data={item?.user} />,
              file_number: item?.files?.file_number,
              amount: `$${item?.amount}`,
              file: (
                <div className="font-bold highlight-text capitalize">
                  {item?.files?.service_name_slug}
                </div>
              ),
              startDate: item?.created_at
                ? moment(item?.created_at).format("lll")
                : "",
              statusText: getStatus(item),
              action: (
                <GlobalTooltip title={"View Invoice"}>
                  <a
                    href={item?.files?.invoice_pdf?.url || "#"}
                    target={item?.files?.invoice_pdf?.url && "_blank"}
                    className="font-medium text-blue-500 w-fit inline-block"
                  >
                    <View />
                  </a>
                </GlobalTooltip>
              ),
            };
          })
        );
        setTotal(data?.total_record);
        setCount(data?.count);
      }
      setIsLoading(false);
    } catch (error) {
      setPaymentList([]);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getServiceList();
  }, []);

  useEffect(() => {
    if (segmentedValue) getPaymentList();
  }, [segmentedValue, currentPage, selectedService, selectedYear, filter]);

  const handleTableChange = (pagination, filters) => {
    setCurrentPage(pagination?.current);
    setFilter(filters);
  };

  const getStatus = (item) => (
    <>
      {segmentedValue === "card" ? (
        <p
          className={`font-bold uppercase ${
            item?.status === "paid" ? "text-green-500" : "text-red-600"
          }`}
        >
          {item?.status}
        </p>
      ) : (
        <>
          {item?.status === "paid" ? (
            <p className="font-bold uppercase text-green-500">{item?.status}</p>
          ) : (
            <>
              {item?.reason ? (
                <p
                  className="font-bold uppercase text-red-600"
                  title={item?.reason}
                >
                  Rejected
                </p>
              ) : (
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => {
                      setSelectedPayment(item);
                      setIsApproveModalOpen(true);
                    }}
                  >
                    <img
                      src="/assets/icons/check.svg"
                      alt="Icon"
                      className="size-5"
                      title="Approve"
                    />
                  </button>
                  <button
                    onClick={() => {
                      setSelectedPayment(item);
                      setIsRejectModalOpen(true);
                    }}
                  >
                    <img
                      src="/assets/icons/close.svg"
                      alt="Icon"
                      className="size-5"
                      title="Reject"
                    />
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </>
  );

  const setStatusData = (status, reason = "") => {
    setPaymentList((prev) => {
      return prev.map((item) => {
        if (item?.id === selectedPayment?.id) {
          const updatedData = { ...item, status, reason };
          return {
            ...updatedData,
            statusText: getStatus(updatedData),
          };
        }
        return item;
      });
    });
  };

  const handleApprove = async () => {
    setIsApproveLoading(true);
    try {
      const { data } = await ApprovePaymentApi({
        file_id: selectedPayment?.file_id,
      });
      if (data?.status) {
        toast.success(data?.message);
        setStatusData("paid");
        setIsApproveModalOpen(false);
      }
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
    } finally {
      setIsApproveLoading(false);
    }
  };

  return (
    <div className="border rounded-xl bg-white">
      <div className="flex flex-col gap-4">
        <div class="p-2 sm:flex items-center justify-between">
          <div className="sm:mb-0 mb-2">
            <MySegmented
              options={paymentType}
              value={segmentedValue}
              setValue={setSegmentedValue}
            />
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <YearPicker
              value={selectedYear}
              onChange={(year) => setSelectedYear(year)}
            />

            <div className="md:w-auto w-40">
              <Search handleSearch={getPaymentList} />
            </div>

            <Select
              placeholder="Select service"
              className="md:w-[200px] w-40"
              options={serviceList}
              value={selectedService}
              onChange={(value) => setSelectedService(value)}
            />
          </div>
        </div>

{userDetails?.role === "admin" && (
  (count?.total_received || count?.total_pending) ? (
    <div className="flex items-center justify-end gap-5 px-5 title">
      <p>Total Received (${count?.total_received || 0})</p>
      <p>Total Pending (${count?.total_pending || 0})</p>
    </div>
  ) : null
)}

        <div className="table-radius-0 text-light mb-4">
          <SkeletonTable
            columns={columns}
            data={paymentList}
            isLoading={isLoading}
            pagination={{
              current: currentPage,
              total: total,
              pageSize: 10,
              showSizeChanger: false,
            }}
            handleTableChange={handleTableChange}
          />
        </div>
      </div>

      <RejectPaymentModal
        isOpen={isRejectModalOpen}
        setIsOpen={setIsRejectModalOpen}
        payment={selectedPayment}
        setStatusData={setStatusData}
      />

      <ConfirmationModal
        type={"success"}
        isOpen={isApproveModalOpen}
        setIsOpen={setIsApproveModalOpen}
        message={`Are you sure you want to approve this payment?`}
        onConfirm={handleApprove}
        isLoading={isApproveLoading}
        loadingLabel={`Approving payment...`}
      />
    </div>
  );
};

export default PaymentsReport;
